import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import { Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import classes from "../LandedCostUploadHistoryTable/LandedCostUploadHistory.module.css";
import { updateLandedCostSkuExclusionHistory } from "../../API/landedCostSkuExclusionHistoryUpdateApi";
import AlertManager from "../Alert/Alert";
import { ensurePercentSuffix, normalizeCurrencyValue } from "../utils/currency";
import { exportRowsToXlsx } from "../utils/exportUtils";

const normalizeTariffFlag = (value) => {
  if (value === null || value === undefined) return "N";

  if (typeof value === "boolean") {
    return value ? "Y" : "N";
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "N";
    return value === 0 ? "N" : "Y";
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return "N";
  if (normalized === "true") return "Y";
  if (normalized === "false") return "N";
  if (normalized === "y" || normalized === "yes") return "Y";
  if (normalized === "n" || normalized === "no") return "N";

  const numeric = Number(normalized);
  if (!Number.isNaN(numeric)) {
    return numeric === 0 ? "N" : "Y";
  }

  return "N";
};

const normalizeSkuExclusionRow = (row = {}) => ({
  ...row,
  tariffSec122: normalizeTariffFlag(row.tariffSec122 ?? row.tariff1),
  tariffSec232: normalizeTariffFlag(row.tariffSec232 ?? row.tariff2),
  tariffSec301: normalizeTariffFlag(row.tariffSec301 ?? row.tariff3),
  sec232Percent: clampSec232Percent(row.sec232Percent) ?? row.sec232Percent,
});

const clampSec232Percent = (value) => {
  const numeric = normalizeCurrencyValue(value);
  if (numeric === null || !Number.isFinite(numeric)) return null;
  if (numeric < -SEC_232_LIMIT) return -SEC_232_LIMIT;
  if (numeric > SEC_232_LIMIT) return SEC_232_LIMIT;
  return numeric;
};

const isPartialDecimal = (value) =>
  typeof value === "string" && /^-?\d*\.?\d*$/.test(value.trim());

const SEC_232_LIMIT = 10000000;

const LandedCostSkuExclusionTable = ({ rows = [], loading = false }) => {
  const [pageSize, setPageSize] = useState(15);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const dirtyRowsRef = useRef(new Map());
  const apiRef = useGridApiRef();

  const [gridRows, setGridRows] = useState(
    Array.isArray(rows) ? rows.map(normalizeSkuExclusionRow) : []
  );

  useEffect(() => {
    const normalizedRows = Array.isArray(rows) ? rows.map(normalizeSkuExclusionRow) : [];
    setGridRows(normalizedRows);
    dirtyRowsRef.current.clear();
    setIsEditing(false);
    setSubmitArmed(false);
  }, [rows]);

  const reloadAfterAlert = useCallback((alertPromise) => {
    if (alertPromise && typeof alertPromise.finally === "function") {
      alertPromise.finally(() => window.location.reload());
    }
  }, []);

  const PercentEditCell = (params) => {
    const { id, field, value } = params;
    const handleChange = (event) => {
      const raw = event.target.value;
      params.api.setEditCellValue({ id, field, value: raw }, event);
    };
    return (
      <input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={handleChange}
        style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "6px" }}
        data-testid={`sec232-edit-${id}`}
      />
    );
  };

  const markRowDirty = useCallback((newRow, oldRow) => {
    if (!newRow || !oldRow) return;
    const changed =
      newRow.napaSku !== oldRow.napaSku ||
      newRow.tariffSec122 !== oldRow.tariffSec122 ||
      newRow.tariffSec232 !== oldRow.tariffSec232 ||
      newRow.tariffSec301 !== oldRow.tariffSec301 ||
      newRow.sec232Percent !== oldRow.sec232Percent;

    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      if (!isEditing) return oldRow;

      const mergedRow = { ...oldRow, ...newRow };
      const normalizedPercent = clampSec232Percent(mergedRow.sec232Percent);
      if (normalizedPercent === null) {
        if (!isPartialDecimal(mergedRow.sec232Percent)) {
          AlertManager.error("Sec 232% must be a valid number.", { contactSupport: false });
        }
        return oldRow;
      }

      const rowToPersist = {
        ...normalizeSkuExclusionRow(mergedRow),
        sec232Percent: normalizedPercent,
      };

      setGridRows((prev) =>
        prev.map((row) => (row.id === rowToPersist.id ? rowToPersist : row))
      );
      markRowDirty(rowToPersist, oldRow);
      return rowToPersist;
    },
    [isEditing, markRowDirty]
  );

  const commitActiveCellEdits = useCallback(async () => {
    const editModel =
      apiRef.current?.getEditRowsModel?.() || apiRef.current?.state?.editRows || {};
    const entries = Object.entries(editModel || {});
    for (const [rowId, fields] of entries) {
      const numericId = Number(rowId);
      const resolvedId = Number.isNaN(numericId) ? rowId : numericId;
      for (const field of Object.keys(fields)) {
        try {
          await Promise.resolve(
            apiRef.current?.commitCellChange?.({ id: resolvedId, field })
          );
          await Promise.resolve(
            apiRef.current?.stopCellEditMode?.({
              id: resolvedId,
              field,
              ignoreModifications: false,
            })
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Unable to commit SKU exclusion edit", err);
        }
      }
    }
  }, [apiRef]);

  const handleEditToggle = useCallback(async () => {
    if (!isEditing && gridRows.length === 0) return;

    if (!isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(true);
      setSubmitArmed(false);
      return;
    }

    if (saving) return;

    await commitActiveCellEdits();

    const payload = Array.from(dirtyRowsRef.current.values());
    if (!payload.length) {
      setIsEditing(false);
      setSubmitArmed(false);
      return;
    }

    try {
      await AlertManager.confirm(`Save ${payload.length} edited row(s)?`, {
        contactSupport: false,
        contactSupportBtn: true,
        closeBtnText: "Save",
        contactBtnText: "Cancel",
      });
    } catch (err) {
      return;
    }

    setSaving(true);
    try {
      const response = await updateLandedCostSkuExclusionHistory(payload);
      if (!response) {
        throw new Error("SKU exclusion update failed");
      }
      dirtyRowsRef.current.clear();
      setIsEditing(false);
      setSubmitArmed(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save SKU exclusion rows", error);
      reloadAfterAlert(AlertManager.error("Failed to update SKU exclusion history."));
    } finally {
      setSaving(false);
    }
  }, [commitActiveCellEdits, gridRows.length, isEditing, saving, reloadAfterAlert]);

  const handleDummySubmit = useCallback(async () => {
    await commitActiveCellEdits();
    setSubmitArmed(true);
  }, [commitActiveCellEdits]);

  const columns = useMemo(
    () => [
      { field: "napaSku", headerName: "Parsed SKU #", flex: 1.2, minWidth: 140},
      {
        field: "sec232Percent",
        headerName: "% Component for Sec 232",
        flex: 1.4,
        minWidth: 180,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
        preProcessEditCellProps: (params) => {
          const raw = params.props?.value;
          const numeric = clampSec232Percent(raw);
          const isValid =
            (numeric !== null && numeric >= -SEC_232_LIMIT && numeric <= SEC_232_LIMIT) ||
            isPartialDecimal(raw);
          return { ...params.props, error: !isValid };
        },
        valueParser: (value, params) => {
          const numeric = clampSec232Percent(value);
          if (numeric === null) {
            if (isPartialDecimal(value)) {
              return value;
            }
            return params?.row?.sec232Percent ?? "";
          }
          return numeric;
        },
        renderEditCell: (params) => <PercentEditCell {...params} />,
      },
      {
        field: "tariffSec122",
        headerName: "Tariff Sec 122",
        flex: 0.9,
        minWidth: 130,
        editable: true,
        type: "singleSelect",
        valueOptions: ["Y", "N"],
        valueFormatter: ({ value }) => normalizeTariffFlag(value),
      },
      {
        field: "tariffSec232",
        headerName: "Tariff Sec 232",
        flex: 0.9,
        minWidth: 130,
        editable: true,
        type: "singleSelect",
        valueOptions: ["Y", "N"],
        valueFormatter: ({ value }) => normalizeTariffFlag(value),
      },
      {
        field: "tariffSec301",
        headerName: "Tariff Sec 301",
        flex: 0.9,
        minWidth: 130,
        editable: true,
        type: "singleSelect",
        valueOptions: ["Y", "N"],
        valueFormatter: ({ value }) => normalizeTariffFlag(value),
      },
    ],
    []
  );

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns,
      rows: gridRows,
      fileName: "sku-exclusion-history.xlsx",
      sheetName: "SKU Exclusion",
      warningMessage: "No SKU exclusion rows to export.",
    });
  }, [columns, gridRows]);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tableActions}>
        <Button
          variant="contained"
          size="small"
          onClick={handleEditToggle}
          disabled={saving || (isEditing && !submitArmed) || gridRows.length === 0}
          className={classes.editButton}
        >
          {saving ? "Saving..." : isEditing ? "Save Changes" : "Edit"}
        </Button>
        {isEditing && (
          <Button
            type="submit"
            size="small"
            variant="text"
            className={classes.dummySubmitBtn}
            data-testid="sku-exclusion-dummy-submit"
            onClick={handleDummySubmit}
          >
            Finish Editing
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={!gridRows.length || loading || saving || isEditing}
          startIcon={<DownloadIcon fontSize="small" />}
          className={classes.exportButton}
          data-testid="sku-exclusion-export-btn"
        >
          Export
        </Button>
      </div>
      <DataGrid
        rows={gridRows}
        columns={columns}
        apiRef={apiRef}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pageSize={pageSize}
        rowsPerPageOptions={[15, 25, 50]}
        onPageSizeChange={(size) => setPageSize(size)}
        editMode="cell"
        autoHeight
        isCellEditable={() => isEditing}
        processRowUpdate={handleProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        onProcessRowUpdateError={(error) => console.error(error)}
        rowHeight={32}
        columnHeaderHeight={36}
        loading={loading || saving}
        components={{ Pagination: CustomPagination }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
            py: 0.25,
          },
          "& .MuiDataGrid-columnHeaders": {
            fontSize: "0.82rem",
          },
        }}
      />
    </div>
  );
};

export default LandedCostSkuExclusionTable;
